import { ListFilter, X } from 'lucide-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../../atoms/badge/badge';
import { Button } from '../../../atoms/button/button';
import { FilterType, filterHasValue, formatFilterBadgeText } from '../filter-utils';

interface IFilterBadgeProps {
  readonly filter: FilterType;
  readonly badgeValue?: string;
  readonly onClearFilter: (filter: FilterType, valueToClear?: string) => void;
}
const FilterBadge = ({ filter, badgeValue, onClearFilter }: IFilterBadgeProps) => {
  const { t } = useTranslation();
  const { createClickHandler, createKeyDownHandler } = useFilterHandlers(onClearFilter);
  return (
    <button
      aria-label={`Remove ${filter.name} filter`}
      className="cursor-pointer appearance-none bg-transparent border-0 p-0 m-0 text-inherit font-inherit"
      key={filter.id}
      onClick={createClickHandler(filter, badgeValue)}
      onKeyDown={createKeyDownHandler(filter, badgeValue)}
      style={{ display: 'inline' }}
      tabIndex={0}
    >
      <Badge className="m-1" key={filter.id} size="lg" variant="secondary">
        {filter.name}: {formatFilterBadgeText(filter, badgeValue)}
        <X aria-hidden="true" className="ml-1" />
        <span className="sr-only">
          {t('ui-components.table.filters.removeFilter', {
            type: filter.name,
          })}
        </span>
      </Badge>
    </button>
  );
};

const FilterBadges: React.FC<IFilterBadgesProps> = ({ filters, onClearFilter, onOpenFilters }) => {
  const { t } = useTranslation();
  if (filters.length === 0) return null;
  const activeFilters = filters.filter(filterHasValue);
  return (
    <div className="flex flex-wrap gap-1">
      {activeFilters.flatMap((filter) => {
        if (filter.kind === 'multi-select' && filter.value) {
          return filter.value.map((value) => (
            <FilterBadge
              badgeValue={value}
              filter={filter}
              key={`${filter.id}-${value}`}
              onClearFilter={onClearFilter}
            />
          ));
        }
        return <FilterBadge filter={filter} key={filter.id} onClearFilter={onClearFilter} />;
      })}
      <Button
        appearance="secondary"
        aria-haspopup="dialog"
        className="m-1"
        icon={ListFilter}
        onClick={onOpenFilters}
        size="sm"
      >
        {activeFilters.length > 0
          ? t('ui-components.table.filters.moreFilters')
          : t('ui-components.table.filters.filters')}
      </Button>
    </div>
  );
};

interface IFilterBadgesProps {
  filters: FilterType[];
  onClearFilter: (filter: FilterType, valueToClear?: string) => void;
  onOpenFilters: () => void;
}

const useFilterHandlers = (onClearFilter: (filter: FilterType, valueToClear?: string) => void) => {
  const createClickHandler = useCallback(
    (filter: FilterType, badgeValue?: string) => {
      return () => onClearFilter(filter, badgeValue);
    },
    [onClearFilter]
  );

  const createKeyDownHandler = useCallback(
    (filter: FilterType, badgeValue?: string) => {
      return (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClearFilter(filter, badgeValue);
        }
      };
    },
    [onClearFilter]
  );

  return {
    createClickHandler,
    createKeyDownHandler,
  };
};

export { FilterBadges, useFilterHandlers };
